import {
  LeadRecord,
  OpportunityRecord,
  LoanOfficer,
  FilterState,
  FunnelMetrics,
  LoanOfficerMetrics,
  FunnelStep,
} from '../types/throughput'
import { isWithinInterval, parseISO } from 'date-fns'

export const filterData = (
  leads: LeadRecord[],
  opportunities: OpportunityRecord[],
  officers: LoanOfficer[],
  filters: FilterState,
) => {
  // 1. Filter Leads by Date Range (Cohort)
  let filteredLeads = leads.filter((lead) => {
    const created = parseISO(lead.createdAt)
    return isWithinInterval(created, {
      start: parseISO(filters.dateRange.start),
      end: parseISO(filters.dateRange.end),
    })
  })

  // 2. Filter by Lead Source
  if (filters.leadSources.length > 0) {
    filteredLeads = filteredLeads.filter((lead) =>
      filters.leadSources.includes(lead.source),
    )
  }

  // 3. Filter by Scope (Officers)
  let scopedOfficerIds: string[] = []

  if (filters.scopeType === 'All') {
    scopedOfficerIds = officers.map((o) => o.id)
  } else if (filters.scopeType === 'SVP' && filters.selectedScopeId) {
    scopedOfficerIds = officers
      .filter(
        (o) =>
          o.svpId === filters.selectedScopeId ||
          o.id === filters.selectedScopeId,
      )
      .map((o) => o.id)
  } else if (filters.scopeType === 'Team Lead' && filters.selectedScopeId) {
    scopedOfficerIds = officers
      .filter(
        (o) =>
          o.teamLeadId === filters.selectedScopeId ||
          o.id === filters.selectedScopeId,
      )
      .map((o) => o.id)
  } else if (filters.scopeType === 'Loan Officer' && filters.selectedScopeId) {
    scopedOfficerIds = [filters.selectedScopeId]
  }

  // Filter out inactive if needed
  if (!filters.includeInactive) {
    const activeIds = new Set(
      officers.filter((o) => o.isActive).map((o) => o.id),
    )
    scopedOfficerIds = scopedOfficerIds.filter((id) => activeIds.has(id))
  }

  filteredLeads = filteredLeads.filter((lead) =>
    scopedOfficerIds.includes(lead.ownerId),
  )

  // Get corresponding opportunities
  const leadIds = new Set(filteredLeads.map((l) => l.id))
  const filteredOpportunities = opportunities.filter((opp) =>
    leadIds.has(opp.leadId),
  )

  return { filteredLeads, filteredOpportunities, scopedOfficerIds }
}

export const calculateFunnelMetrics = (
  leads: LeadRecord[],
  opportunities: OpportunityRecord[],
): FunnelMetrics[] => {
  const counts = {
    Created: leads.length,
    Converted: opportunities.length,
    'App Out': opportunities.filter((o) => o.appOutDate).length,
    'App Completed': opportunities.filter((o) => o.appCompletedDate).length,
    'Disclosures Sent': opportunities.filter((o) => o.disclosuresSentDate)
      .length,
    'Disclosures Received': opportunities.filter(
      (o) => o.disclosuresReceivedDate,
    ).length,
    'Submitted to UW': opportunities.filter((o) => o.submittedToUWDate).length,
    'Approved with Conditions': opportunities.filter(
      (o) => o.approvedWithConditionsDate,
    ).length,
    'Closing Scheduled': opportunities.filter((o) => o.closingScheduledDate)
      .length,
    Funded: opportunities.filter((o) => o.fundedDate).length,
  }

  const steps: FunnelStep[] = [
    'Created',
    'Converted',
    'App Out',
    'App Completed',
    'Disclosures Sent',
    'Disclosures Received',
    'Submitted to UW',
    'Approved with Conditions',
    'Closing Scheduled',
    'Funded',
  ]

  return steps.map((step, index) => {
    const count = counts[step]
    const prevCount = index === 0 ? count : counts[steps[index - 1]]
    const createdCount = counts['Created']

    return {
      step,
      count,
      conversionRate: prevCount > 0 ? (count / prevCount) * 100 : 0,
      overallConversionRate:
        createdCount > 0 ? (count / createdCount) * 100 : 0,
    }
  })
}

export const calculateOfficerMetrics = (
  officers: LoanOfficer[],
  leads: LeadRecord[],
  opportunities: OpportunityRecord[],
): LoanOfficerMetrics[] => {
  return officers.map((officer) => {
    const officerLeads = leads.filter((l) => l.ownerId === officer.id)
    const officerOpps = opportunities.filter((o) => o.ownerId === officer.id)

    const counts = {
      Created: officerLeads.length,
      Converted: officerOpps.length,
      'App Out': officerOpps.filter((o) => o.appOutDate).length,
      'App Completed': officerOpps.filter((o) => o.appCompletedDate).length,
      'Disclosures Sent': officerOpps.filter((o) => o.disclosuresSentDate)
        .length,
      'Disclosures Received': officerOpps.filter(
        (o) => o.disclosuresReceivedDate,
      ).length,
      'Submitted to UW': officerOpps.filter((o) => o.submittedToUWDate).length,
      'Approved with Conditions': officerOpps.filter(
        (o) => o.approvedWithConditionsDate,
      ).length,
      'Closing Scheduled': officerOpps.filter((o) => o.closingScheduledDate)
        .length,
      Funded: officerOpps.filter((o) => o.fundedDate).length,
    }

    const submittedOpps = officerOpps.filter((o) => o.submittedToUWDate)
    const cleanFileCount = submittedOpps.filter(
      (o) => !o.isDirtyAtSubmission,
    ).length

    return {
      officer,
      counts,
      conversion: {
        createdToConverted:
          counts['Created'] > 0
            ? (counts['Converted'] / counts['Created']) * 100
            : 0,
        createdToFunded:
          counts['Created'] > 0
            ? (counts['Funded'] / counts['Created']) * 100
            : 0,
      },
      cleanFiles: {
        count: cleanFileCount,
        total: submittedOpps.length,
        percentage:
          submittedOpps.length > 0
            ? (cleanFileCount / submittedOpps.length) * 100
            : 0,
      },
    }
  })
}
